import React, { useEffect, useMemo, useState } from 'react';
import { useNotification } from '../../context/NotificationContext';
import './ConcienciaLetterPanel.css';

function hasReplayableGrid(item) {
  return Array.isArray(item?.grid) && item.grid.length > 0 && Array.isArray(item.grid[0]);
}

function MazeMiniPreview({ maze, t }) {
  if (!hasReplayableGrid(maze)) {
    return <p className="conciencia-maze-no-map">{t('consciousness.mazeNoStoredMap')}</p>;
  }

  return (
    <div
      className="conciencia-maze-preview-grid"
      style={{ '--maze-preview-columns': maze.grid[0].length }}
      aria-hidden="true"
    >
      {maze.grid.flatMap((row, rowIndex) => row.map((cell, colIndex) => {
        const className = [
          'conciencia-maze-preview-cell',
          Number(cell) === 1 ? 'is-wall' : 'is-path',
          Number(cell) === 2 ? 'is-start' : '',
          Number(cell) === 3 ? 'is-target' : ''
        ].filter(Boolean).join(' ');
        return <span key={rowIndex + '-' + colIndex} className={className} />;
      }))}
    </div>
  );
}

function getResultLabel(t, result) {
  const normalized = String(result || 'unknown');
  const key = 'consciousness.mazeHistoryResult' + normalized.charAt(0).toUpperCase() + normalized.slice(1);
  return t(key);
}

function getSourceLabel(t, source) {
  const normalized = String(source || 'unlock');
  const key = 'consciousness.mazeSource' + normalized.charAt(0).toUpperCase() + normalized.slice(1);
  return t(key);
}

function confirmMessage(t, key) {
  return t(key) + ' ' + t('consciousness.deleteIrreversible');
}

export default function ConcienciaLetterPanel({
  letter,
  letters = [],
  mazeHistory = [],
  cooldownUntil,
  currentStamina = 0,
  staminaMax = 100,
  onReturn,
  onOpenHistory,
  showHistory,
  onCloseHistory,
  onOpenMazeHistory,
  showMazeHistory,
  onCloseMazeHistory,
  onReplayMaze,
  onDeleteMaze,
  onDeleteAllMazes,
  onDeleteLetter,
  onDeleteAllLetters,
  onGenerateMaze,
  onPlayGeneratedMaze,
  onDiscardGeneratedMaze,
  generatedMazeDraft,
  mazeActionError = '',
  startingMazeId = '',
  t
}) {
  const { addConfirm } = useNotification();
  const [now, setNow] = useState(Date.now());
  const [selectedComplexity, setSelectedComplexity] = useState('low');

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const remainingSeconds = useMemo(() => {
    if (!cooldownUntil) return 0;
    return Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  }, [cooldownUntil, now]);
  const canReturn = remainingSeconds <= 0;
  const hasCooldown = Boolean(cooldownUntil);
  const visualRecharge = Math.min(Number(staminaMax) || 100, Math.max(0, Math.round(Number(currentStamina) || 0)));
  const rechargeProgress = Math.min(1, Math.max(0, visualRecharge / (Number(staminaMax) || 100)));
  const currentLetter = letter || {
    title: t('consciousness.letterTitle'),
    body: t('consciousness.letterFallbackBody', {
      question: t('consciousness.letterUnknownQuestion'),
      response: t('consciousness.letterUnknownResponse')
    })
  };

  const requestConfirm = (messageKey, onConfirm) => {
    addConfirm(confirmMessage(t, messageKey), onConfirm);
  };

  const handleDeleteLetter = (id) => requestConfirm('consciousness.letterDeleteConfirm', () => onDeleteLetter?.(id));
  const handleDeleteAllLetters = () => requestConfirm('consciousness.letterDeleteAllConfirm', () => onDeleteAllLetters?.());
  const handleDeleteMaze = (id) => requestConfirm('consciousness.mazeDeleteConfirm', () => onDeleteMaze?.(id));
  const handleDeleteAllMazes = () => requestConfirm('consciousness.mazeDeleteAllConfirm', () => onDeleteAllMazes?.());

  const switchToLetterHistory = () => onOpenHistory?.();
  const switchToMazeHistory = () => onOpenMazeHistory?.();

  return (
    <section className="conciencia-letter-shell" aria-live="polite">
      <div className="conciencia-letter-main">
        <p className="conciencia-letter-kicker">{t('consciousness.letterKicker')}</p>
        <h2>{currentLetter.title || t('consciousness.letterTitle')}</h2>
        <p>{currentLetter.body}</p>
        <div className="conciencia-letter-wait">
          {canReturn ? t('consciousness.letterReady') : t('consciousness.letterWait', { seconds: remainingSeconds })}
        </div>
        {hasCooldown && (
          <div className={'conciencia-recharge-ritual' + (canReturn ? ' is-ready' : '')}>
            <div className="conciencia-recharge-orb" aria-hidden="true">
              <span className="conciencia-recharge-smoke" />
            </div>
            <div className="conciencia-recharge-copy">
              <strong>{t('consciousness.rechargeTitle')}</strong>
              <span>{canReturn ? t('consciousness.rechargeReady') : t('consciousness.rechargeBody')}</span>
              <span>{t('consciousness.rechargeEnergy', { value: visualRecharge })}</span>
              <div className="conciencia-recharge-progress" aria-hidden="true">
                <div className="conciencia-recharge-fill" style={{ width: (rechargeProgress * 100) + '%' }} />
              </div>
            </div>
          </div>
        )}
        <div className="conciencia-letter-actions">
          <button type="button" className="regenerate-btn" onClick={onReturn} disabled={!canReturn}>
            {t('consciousness.letterReturn')}
          </button>
          <button type="button" className="conciencia-secondary-btn" onClick={onOpenHistory}>
            {t('consciousness.letterHistoryButton')}
          </button>
          <button type="button" className="conciencia-secondary-btn" onClick={onOpenMazeHistory}>
            {t('consciousness.mazeHistoryButton')}
          </button>
        </div>
      </div>

      {(showHistory || showMazeHistory) && (
        <div className="conciencia-letter-history" role="dialog" aria-modal="true" aria-label={showMazeHistory ? t('consciousness.mazeHistoryTitle') : t('consciousness.letterHistoryTitle')}>
          <div className={'conciencia-letter-history-panel' + (showMazeHistory ? ' conciencia-maze-history-panel' : '')}>
            <div className="conciencia-letter-history-header">
              <h3>{showMazeHistory ? t('consciousness.mazeHistoryTitle') : t('consciousness.letterHistoryTitle')}</h3>
              <button type="button" onClick={showMazeHistory ? onCloseMazeHistory : onCloseHistory} aria-label={showMazeHistory ? t('consciousness.mazeHistoryClose') : t('consciousness.letterHistoryClose')} title={showMazeHistory ? t('consciousness.mazeHistoryClose') : t('consciousness.letterHistoryClose')}><span aria-hidden="true">&times;</span></button>
            </div>
            <div className="conciencia-history-tabs" role="tablist" aria-label={t('consciousness.historyTabsLabel')}>
              <button type="button" className={showHistory ? 'is-active' : ''} role="tab" aria-selected={showHistory} onClick={switchToLetterHistory}>{t('consciousness.letterHistoryTab')}</button>
              <button type="button" className={showMazeHistory ? 'is-active' : ''} role="tab" aria-selected={showMazeHistory} onClick={switchToMazeHistory}>{t('consciousness.mazeHistoryTab')}</button>
            </div>

            {showHistory && (
              <>
                {letters.length === 0 ? (
                  <p className="conciencia-letter-empty">{t('consciousness.letterHistoryEmpty')}</p>
                ) : (
                  <>
                    <ul className="conciencia-letter-list">
                      {letters.map((item) => (
                        <li key={item.id}>
                          <div className="conciencia-letter-history-item-head">
                            <div>
                              <strong>{item.title}</strong>
                              <time>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}</time>
                            </div>
                            <button type="button" className="conciencia-secondary-btn danger" onClick={() => handleDeleteLetter(item.id)}>
                              {t('consciousness.letterDelete')}
                            </button>
                          </div>
                          <p>{item.body}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="conciencia-history-bottom-actions">
                      <button type="button" className="conciencia-secondary-btn danger" onClick={handleDeleteAllLetters}>
                        {t('consciousness.letterDeleteAll')}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {showMazeHistory && (
              <>
                <div className="conciencia-maze-generator">
                  <div className="conciencia-maze-generator-head">
                    <div>
                      <strong>{t('consciousness.mazeGenerateTitle')}</strong>
                      <p>{t('consciousness.mazeGenerateSubtitle')}</p>
                    </div>
                    <button type="button" className="conciencia-secondary-btn primary" onClick={() => onGenerateMaze?.(selectedComplexity)}>
                      {t('consciousness.mazeGeneratePrimary')}
                    </button>
                  </div>
                  <div className="conciencia-maze-complexity" role="group" aria-label={t('consciousness.mazeComplexityLabel')}>
                    {['low', 'medium', 'high', 'extreme'].map((level) => (
                      <button
                        key={level}
                        type="button"
                        className={selectedComplexity === level ? 'is-active' : ''}
                        onClick={() => setSelectedComplexity(level)}
                      >
                        {t('consciousness.mazeComplexity' + level.charAt(0).toUpperCase() + level.slice(1))}
                      </button>
                    ))}
                  </div>
                  {generatedMazeDraft && (
                    <div className="conciencia-generated-maze-preview">
                      <div>
                        <span className="conciencia-maze-source-badge is-generated">{t('consciousness.mazeGeneratedPreview')}</span>
                        <MazeMiniPreview maze={generatedMazeDraft} t={t} />
                      </div>
                      <div className="conciencia-generated-maze-actions">
                        <button type="button" className="regenerate-btn" onClick={onPlayGeneratedMaze}>
                          {t('consciousness.mazePlayGenerated')}
                        </button>
                        <button type="button" className="conciencia-secondary-btn" onClick={onDiscardGeneratedMaze}>
                          {t('consciousness.mazeDiscardGenerated')}
                        </button>
                      </div>
                    </div>
                  )}
                  {mazeActionError && <p className="conciencia-maze-action-error">{mazeActionError}</p>}
                </div>

                <div className="conciencia-maze-history-section-title">
                  <h4>{t('consciousness.mazeHistoryTitle')}</h4>
                </div>

                {mazeHistory.length === 0 ? (
                  <p className="conciencia-letter-empty">{t('consciousness.mazeHistoryEmpty')}</p>
                ) : (
                  <>
                    <ul className="conciencia-letter-list conciencia-maze-history-list">
                      {mazeHistory.map((item) => {
                        const canReplay = hasReplayableGrid(item);
                        const source = item.source || 'unlock';
                        return (
                          <li key={item.id} className={'maze-source-' + source}>
                            <div className="conciencia-maze-history-item-head">
                              <div>
                                <strong>{t('consciousness.mazeHistoryMeta', { result: getResultLabel(t, item.result), difficulty: item.difficulty, seconds: item.durationSeconds })}</strong>
                                <time>{item.playedAt || item.createdAt ? new Date(item.playedAt || item.createdAt).toLocaleString() : ''}</time>
                              </div>
                              <span className={'conciencia-maze-source-badge is-' + source}>{getSourceLabel(t, source)}</span>
                            </div>
                            <MazeMiniPreview maze={item} t={t} />
                            {!canReplay && <p className="conciencia-maze-no-map">{t('consciousness.mazeNoStoredMap')}</p>}
                            <div className="conciencia-maze-run-actions">
                              <button
                                type="button"
                                className="conciencia-secondary-btn"
                                onClick={() => onReplayMaze?.(item)}
                                disabled={!canReplay || startingMazeId === item.id}
                              >
                                {startingMazeId === item.id ? t('consciousness.mazeStartingReplay') : t('consciousness.mazeReplayButton')}
                              </button>
                              <button type="button" className="conciencia-secondary-btn danger" onClick={() => handleDeleteMaze(item.id)}>
                                {t('consciousness.mazeDelete')}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="conciencia-history-bottom-actions">
                      <button type="button" className="conciencia-secondary-btn danger" onClick={handleDeleteAllMazes}>
                        {t('consciousness.mazeDeleteAll')}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
